<template>
  <v-card>
    <v-card-text>
      <h2><v-icon>room</v-icon>Address</h2>
      <div style="white-space: pre-wrap">
        <span v-if="entity.addressLink">{{
          entity.addressLink.address.address
        }}</span>
        <span v-if="!entity.addressLink">EMPTY</span>
      </div>
      <GmapMap
        :center="{ lat: mapModel.latLng.lat, lng: mapModel.latLng.lng }"
        :zoom="18"
        map-type-id="hybrid"
        style="width: 100%; height: 400px"
        v-if="mapModel"
      >
        <GmapMarker
          :position="mapModel.position"
          :clickable="true"
          :draggable="true"
          :title="mapModel.title"
          @click="center = mapModel.position"
        />
      </GmapMap>
      <v-btn v-if="!editAddress" @click="loadSuggestions">Change address</v-btn>
      <div class="ajaxForm" v-if="editAddress">
        <h3>Change address</h3>
        <template v-if="editAddressStatus == 0">
          <template v-if="addressSuggestions && addressSuggestions.length > 0">
            <p>
              Here are some suggested addresses based on related entities in the
              addressbook. Please check first whether the address you want is
              listed here. Click an address to use it:
            </p>
            <ul>
              <li
                v-for="addressSuggestion in addressSuggestions"
                :key="addressSuggestion.id"
              >
                <a @click="saveSuggestion(addressSuggestion)">{{
                  addressSuggestion.address
                }}</a>
              </li>
            </ul>
            <p>
              If the address you want is not listed please select from the
              following options:
            </p>
          </template>
          <ul>
            <li>
              <a @click="editAddressStatus = 1">Search for address</a>
            </li>
            <li>
              <a @click="loadAddress">Edit address manually</a>
            </li>
          </ul>
        </template>
        <template v-if="editAddressStatus == 1">
          <Postcode @changeAddress="select" />
        </template>
        <template v-if="editAddressStatus == 2">
          <v-form ref="addressEditForm">
            <template v-if="addressEdit.version == 1">
              <span>Edit address manually</span>
              <v-text-field
                label="PCA id"
                v-model="addressEdit.pcaid"
                readonly
              />
              <v-text-field
                label="PO Box"
                v-model="addressEdit.pobox"
                :rules="lengthRules"
                :counter="60"
              />
              <v-text-field
                label="Sub building"
                v-model="addressEdit.subbuilding"
                :rules="lengthRules"
                :counter="60"
              />
              <v-text-field
                label="Building name"
                v-model="addressEdit.buildingname"
                :rules="lengthRules"
                :counter="60"
              />
              <v-text-field
                label="Building number"
                v-model="addressEdit.buildingnumber"
                :rules="lengthRules"
                :counter="60"
              />
              <v-text-field
                label="Primary street"
                v-model="addressEdit.primarystreet"
                :rules="lengthRules"
                :counter="60"
              />
              <v-text-field
                label="Secondary street"
                v-model="addressEdit.secondarystreet"
                :rules="lengthRules"
                :counter="60"
              />
              <v-text-field
                label="Double dependent locality"
                v-model="addressEdit.doubledependentlocality"
                :rules="lengthRules"
                :counter="60"
              />
              <v-text-field
                label="Dependent locality"
                v-model="addressEdit.dependentlocality"
                :rules="lengthRules"
                :counter="60"
              />
              <v-text-field
                label="Post town"
                v-model="addressEdit.posttown"
                :rules="lengthRules"
                :counter="60"
              />
              <v-text-field
                label="County"
                v-model="addressEdit.county"
                :rules="lengthRules"
                :counter="60"
              />
              <v-text-field
                label="Postcode"
                v-model="addressEdit.postcode"
                :rules="lengthRules"
                :counter="60"
              />
            </template>
            <template v-if="addressEdit.version != 1">
              <v-textarea label="Address" v-model="addressEdit.address" />
            </template>
            <v-btn
              @click="saveAddress"
              :disabled="addressSaving"
              :loading="addressSaving"
              >Save</v-btn
            >
          </v-form>
        </template>
        <hr />
        <v-btn
          @click="
            editAddress = false;
            editAddressStatus = 0;
          "
          >Cancel</v-btn
        >
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import CREATE_ENTITY_ADDRESS_FROM_PCA_ID from "@/graphql/directory/CreateEntityAddressFromPcaId";
import ENTITY_ADDRESS_SUGGESTIONS from "@/graphql/directory/EntityAddressSuggestions";
import UPDATE_ENTITY_ADDRESS from "@/graphql/directory/UpdateEntityAddress";
import UPDATE_ENTITY_ADDRESS_SUGGESTION from "@/graphql/directory/UpdateEntityAddressSuggestion";
import Postcode from "@/components/layout/Postcode";
export default {
  name: "Address",
  metaInfo() {
    if (!this.entity.typeid || this.entity.typeid != 5) {
      return undefined;
    }
    const title = this.entity.addressLink
      ? this.entity.addressLink.address.address
      : this.$route.params.id;
    return {
      title,
      titleTemplate: "%s - Directory - Conveyancing Tracker",
      htmlAttrs: {
        lang: "en",
        amp: true
      }
    };
  },
  components: { Postcode },
  props: ["entity", "lastUpdated"],
  data() {
    return {
      addressLink: null,
      editAddressStatus: 0,
      editAddress: false,
      addressSuggestions: [],
      addressEdit: null,
      mapModel: null,
      lengthRules: [
        (v) =>
          !v || (v && v.length <= 60) || "This must be less than 60 characters"
      ],
      addressSaving: false
    };
  },
  methods: {
    async loadSuggestions() {
      //load suggestions
      this.editAddress = true;
      const response = await this.$apollo.query({
        query: ENTITY_ADDRESS_SUGGESTIONS,
        variables: {
          id: Number(this.$route.params.id)
        }
      });
      // console.log(response.data);
      this.addressSuggestions = response.data.entity.addressSuggestions;
    },
    loadAddress() {
      if (this.entity.addressLink && this.entity.addressLink.address) {
        this.addressEdit = JSON.parse(
          JSON.stringify(this.entity.addressLink.address)
        );
      } else {
        this.addressEdit = {
          pcaid: null,
          pobox: null
        };
      }
      this.editAddressStatus = 2;
    },
    async saveAddress() {
      if (this.$refs.addressEditForm.validate()) {
        try {
          this.addressSaving = true;
          await this.$apollo.mutate({
            mutation: UPDATE_ENTITY_ADDRESS,
            variables: {
              id: Number(this.$route.params.id),
              address: this.addressEdit
            }
          });
          // console.log(response.data);
          this.$emit("triggerUpdate");
        } catch (err) {
          console.error(err);
          this.addressSaving = false;
        }
      }
    },
    async saveSuggestion(addressSuggestion) {
      await this.$apollo.mutate({
        mutation: UPDATE_ENTITY_ADDRESS_SUGGESTION,
        variables: {
          id: Number(this.$route.params.id),
          addressid: addressSuggestion.id
        }
      });
      // console.log(response.data);
      this.$emit("triggerUpdate");
    },
    async select(pcaid) {
      if (confirm("Are you sure?")) {
        await this.$apollo.mutate({
          mutation: CREATE_ENTITY_ADDRESS_FROM_PCA_ID,
          variables: {
            id: Number(this.$route.params.id),
            pcaid
          }
        });
        // console.log(response.data);
        this.$emit("triggerUpdate");
      }
    }
  },
  watch: {
    lastUpdated: {
      immediate: true,
      handler() {
        this.editAddress = false;
        this.editAddressStatus = 0;
        this.addressSaving = false;
      }
    },
    entity: {
      handler() {
        if (
          !this.entity.addressLink ||
          !this.entity.addressLink.address ||
          !this.entity.addressLink.address.wgs84latitude ||
          !this.entity.addressLink.address.wgs84longitude
        ) {
          this.mapModel = null;
          return;
        }
        const lat = Number(this.entity.addressLink.address.wgs84latitude);
        const lng = Number(this.entity.addressLink.address.wgs84longitude);
        const title = this.entity.addressLink.address.address;
        this.mapModel = {
          latLng: {
            lat,
            lng
          },
          position: {
            lat,
            lng
          },
          title
        };
      }
    }
  }
};
</script>
